import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import ScrollToTop from "./components/ScrollToTop";
import SkipToMain from "./components/SkipToMain";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { CookieConsentBanner } from "./components/CookieConsentBanner";
import LandingPage from "./pages/LandingPage";
import PortfolioPage from "./pages/PortfolioPage";
import TeamPage from "./pages/TeamPage";
import TeamMemberPage from "./pages/TeamMemberPage";
import InsightsPage from "./pages/InsightsPage";
import InsightDetailPage from "./pages/InsightDetailPage";
import InvestorsPage from "./pages/InvestorsPage";
import FoundersPage from "./pages/FoundersPage";
import AboutPage from "./pages/AboutPage";
import EsgPage from "./pages/EsgPage";
import ResourcesPage from "./pages/ResourcesPage";
import ResourceDetailPage from "./pages/ResourceDetailPage";
import NewsPage from "./pages/NewsPage";
import EventsPage from "./pages/EventsPage";
import ComingSoonPage from "./pages/ComingSoonPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import SebiRegistrationDetailsPage from "./pages/SebiRegistrationDetailsPage";
import InvestorAlertPage from "./pages/InvestorAlertPage";
import SiteBannerSettings from "./pages/admin/SiteBannerSettings";
import NewsletterSubscribers from "./pages/admin/NewsletterSubscribers";

// Admin components
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./components/admin/AdminLayout";
import ProtectedRoute from "./components/admin/ProtectedRoute";
import AdminDashboard from "./pages/admin/AdminDashboard";
import PortfolioList from "./pages/admin/PortfolioList";
import PortfolioForm from "./pages/admin/PortfolioForm";
import TeamList from "./pages/admin/TeamList";
import TeamForm from "./pages/admin/TeamForm";
import UserManagement from "./pages/admin/UserManagement";
import FundsManagement from "./pages/admin/FundsManagement";
import { InvestorsList, PartnersList, NewsList, EventsList } from "./pages/admin/CrudLists";
import { InvestorForm, PartnerForm, NewsForm } from "./pages/admin/CrudForms";
import EventForm from "./pages/admin/EventForm";
import BlogList from "./pages/admin/BlogList";
import BlogForm from "./pages/admin/BlogForm";
import MediaList from "./pages/admin/MediaList";
import ResourcesList from "./pages/admin/ResourcesList";
import ResourceForm from "./pages/admin/ResourceForm";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CookieConsentProvider>
          <BrowserRouter>
            <SkipToMain />
            <GoogleAnalytics />
            <CookieConsentBanner />
            <ScrollToTop />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/team/:memberId" element={<TeamMemberPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightDetailPage />} />
            <Route path="/investors" element={<InvestorsPage />} />
            <Route path="/founders" element={<FoundersPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/about/esg" element={<EsgPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/newsletters/:id" element={<ResourceDetailPage />} />
            <Route path="/resources/regulatory-updates/:id" element={<ResourceDetailPage />} />
            <Route path="/resources/representations/:id" element={<ResourceDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            <Route path="/sebi-registration-details" element={<SebiRegistrationDetailsPage />} />
            <Route path="/investor-alert" element={<InvestorAlertPage />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              
              {/* Portfolio */}
              <Route path="portfolio" element={<PortfolioList />} />
              <Route path="portfolio/new" element={<PortfolioForm />} />
              <Route path="portfolio/:id" element={<PortfolioForm />} />
              
              {/* Team */}
              <Route path="team" element={<TeamList />} />
              <Route path="team/new" element={<TeamForm />} />
              <Route path="team/:id" element={<TeamForm />} />
              
              {/* Funds */}
              <Route path="funds" element={<FundsManagement />} />
              
              {/* Investors */}
              <Route path="investors" element={<InvestorsList />} />
              <Route path="investors/new" element={<InvestorForm />} />
              <Route path="investors/:id" element={<InvestorForm />} />
              
              {/* Partners */}
              <Route path="partners" element={<PartnersList />} />
              <Route path="partners/new" element={<PartnerForm />} />
              <Route path="partners/:id" element={<PartnerForm />} />
              
              {/* Blog */}
              <Route path="blog" element={<BlogList />} />
              <Route path="blog/new" element={<BlogForm />} />
              <Route path="blog/:id" element={<BlogForm />} />
              
              {/* Media */}
              <Route path="media" element={<MediaList />} />
              
              {/* News */}
              <Route path="news" element={<NewsList />} />
              <Route path="news/new" element={<NewsForm />} />
              <Route path="news/:id" element={<NewsForm />} />
              
              {/* Events */}
              <Route path="events" element={<EventsList />} />
              <Route path="events/new" element={<EventForm />} />
              <Route path="events/:id" element={<EventForm />} />
              
              {/* Resources (Newsletters + Regulatory Updates) */}
              <Route path="resources" element={<ResourcesList />} />
              <Route path="resources/new" element={<ResourceForm />} />
              <Route path="resources/newsletters/:id" element={<ResourceForm />} />
              <Route path="resources/regulatory-updates/:id" element={<ResourceForm />} />
              <Route path="resources/representations/:id" element={<ResourceForm />} />
              
              {/* Site Banner (black bar at the top of the website) */}
              <Route path="site-banner" element={<SiteBannerSettings />} />

              {/* Newsletter signups (homepage form) */}
              <Route path="newsletter-subscribers" element={<NewsletterSubscribers />} />

              {/* User Management (Super Admin only) */}
              <Route path="users" element={<UserManagement />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </CookieConsentProvider>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
